import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { useGetProjectQuery } from "../../../app/services/projects";
import { useGetCurrentUserQuery } from "../../../app/accounts/authuser";
import { useProjectFieldSync } from "../../../hooks/useProjectFieldSync";
import Input from "../../../components/form-item/Input";
import Select from "../../../components/form-item/Select";
import IconButton from "../../../components/button/IconButton";
import ListBlock from "../../../components/block/ListBlock";
import TitleBlock from "../../../components/block/TitleBlock";
import FieldListHeader from "../../../components/field-list/FieldListHeader";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";
import SvgLoadingAnimation from "../../../components/loading/index";

const SORT_RULE_OPTIONS = [
  { value: "newest", name: "新しい順" },
  { value: "name_asc", name: "名前順" },
];

const TAG_COLUMNS = [
  { label: "タグ名", flex: 1 },
  { label: "並び順", width: 120 },
];

const SortableTagRow = ({ tag, index, onTagChange, onDeleteTag }) => {
  const sortableId = tag.id || `new-${index}`;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="u-w100 u-row u-gap8"
    >
      <div
        style={{
          flexShrink: 0,
          cursor: isDragging ? "grabbing" : "grab",
          color: "var(--soremo-light-gray)",
          display: "flex",
          alignItems: "center",
        }}
        {...attributes}
        {...listeners}
      >
        <span className="material-symbols-rounded" style={{ fontSize: 20 }}>
          drag_indicator
        </span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Input
          type="text"
          placeholder="タグ名"
          value={tag.name}
          onChange={(e) => onTagChange(index, "name", e.target.value)}
        />
      </div>
      <div style={{ width: 120, flexShrink: 0 }}>
        <Select
          value={tag.sort_rule}
          onChange={(e) => onTagChange(index, "sort_rule", e.target.value)}
          options={SORT_RULE_OPTIONS}
        />
      </div>
      <IconButton onClick={() => onDeleteTag(index)} icon="delete" />
    </div>
  );
};

const ProjectShotTags = () => {
  const { projectId } = useParams();
  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: projectData, isLoading } = useGetProjectQuery(
    { project_id: projectId },
    { skip: !projectId || !currentUserData }
  );
  const { updateStatus, debouncedSave } = useProjectFieldSync(projectId);

  const [tags, setTags] = useState([]);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!projectData?.data?.project) return;
    const apiTags = (projectData.data.project.shot_tags || []).map((t) => ({
      id: t.id,
      name: t.name || "",
      sort_rule: t.sort_rule || "newest",
    }));
    if (!initializedRef.current) {
      setTags(apiTags);
      initializedRef.current = true;
      return;
    }
    // 保存後に再取得したプロジェクトから、新規追加したタグ（id: null）へサーバー採番の id を取り込む。
    // 取り込まないと以後の保存で同名を id 無しで送り続け、サーバー側で既存タグ名と衝突する
    // （unique_project_tag_name）。名前の照合は DB の照合順序に合わせて大文字小文字を区別しない。
    setTags((prev) => {
      const claimed = new Set(prev.map((t) => t.id).filter(Boolean));
      let changed = false;
      const next = prev.map((t) => {
        if (t.id) return t;
        const match = apiTags.find(
          (a) => !claimed.has(a.id) && a.name.toLowerCase() === t.name.trim().toLowerCase()
        );
        if (!match) return t;
        claimed.add(match.id);
        changed = true;
        return { ...t, id: match.id };
      });
      return changed ? next : prev;
    });
  }, [projectData]);

  const saveTags = useCallback(
    (updated) => {
      const payload = updated
        .filter((t) => t.id || t.name.trim())
        .map((t) => {
          const item = { name: t.name, sort_rule: t.sort_rule };
          if (t.id) item.id = t.id;
          return item;
        });
      debouncedSave({ shot_tags: payload });
    },
    [debouncedSave]
  );

  const handleTagChange = (index, field, value) => {
    const next = [...tags];
    next[index] = { ...next[index], [field]: value };
    setTags(next);
    saveTags(next);
  };

  const handleAddTag = () => {
    const next = [...tags, { id: null, name: "", sort_rule: "newest" }];
    setTags(next);
  };

  const handleDeleteTag = (index) => {
    const next = tags.filter((_, i) => i !== index);
    setTags(next);
    saveTags(next);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const sortableIds = useMemo(
    () => tags.map((t, i) => t.id || `new-${i}`),
    [tags]
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = sortableIds.indexOf(active.id);
      const newIndex = sortableIds.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(tags, oldIndex, newIndex);
      setTags(reordered);
      saveTags(reordered);
    },
    [tags, sortableIds, saveTags]
  );

  if (isLoading) return <SvgLoadingAnimation />;

  return (
    <div className="u-col u-w100 u-gap16">
      <TitleBlock
        title="タグ"
        description="成果物を分類するタグを設定できます。並び順を指定したり、ドラッグで順番を入れ替えられます。"
      />
      <div className="u-w100 u-col u-gap8">
        {tags.length > 0 && (
          <FieldListHeader
            columns={TAG_COLUMNS}
            handleWidth={20}
            actionWidth={24}
          />
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {tags.map((tag, index) => (
              <SortableTagRow
                key={tag.id || `new-${index}`}
                tag={tag}
                index={index}
                onTagChange={handleTagChange}
                onDeleteTag={handleDeleteTag}
              />
            ))}
          </SortableContext>
        </DndContext>
        <ListBlock type="add" label="タグを追加" onClick={handleAddTag} />
      </div>
      <StatusIndicator status={updateStatus} />
    </div>
  );
};

export default ProjectShotTags;
